// Elimination watcher — fires the epitaph modal when a bot corporation
// transitions from alive to `eliminated: true` in the leaderboard.
//
// Wired into `main.ts` on every `state_update`:
//     checkEliminations(fixture);
//
// For v0.1 we don't have a dedicated `player_eliminated` WS event yet
// (tracked as open point #2 in SPEC_bot_personalities.md §10), so we detect
// transitions by diffing `fixture.leaderboard[].eliminated` against the
// previous snapshot. Works for bankruptcies, FCA indictments, and endgame
// defeats uniformly — anything that flips the flag triggers the obituary.
//
// Human players have no `corpKey` in the bot-corps catalog and therefore
// no epitaph string — `showEpitaph` silently drops them (see epitaph-modal.ts).

import type { ClientFixture } from '../data/fixture';
import { getBotCorps } from '../api';
import type { BotCorp } from '../api';
import { showEpitaph } from './epitaph-modal';

// Map of corp name → corp key, lazily populated from /api/botcorps.
let nameToKey: Map<string, string> | null = null;
// Previous snapshot of names we saw flagged as eliminated. Entries are not
// removed — once eliminated, a corp stays eliminated for the session.
const seenEliminated = new Set<string>();

/**
 * Fetch the bot corporations catalog so we can resolve names to keys when an
 * elimination fires. Safe to call multiple times — only the first fetch hits
 * the network. Failures are swallowed: if the catalog can't load, we fall
 * back to no epitaphs (the game continues normally).
 */
export async function initEliminationWatch(): Promise<void> {
  if (nameToKey !== null) return;
  try {
    const corps: BotCorp[] = await getBotCorps();
    const map = new Map<string, string>();
    for (const c of corps) {
      if (c.key) map.set(c.name, c.key);
    }
    nameToKey = map;
  } catch {
    nameToKey = new Map(); // sentinel: "we tried, move on"
  }
}

/**
 * Test-only: clear the cached catalog and reset seen-eliminated state so
 * each test starts from a clean slate.
 */
export function _resetEliminationWatch(): void {
  nameToKey = null;
  seenEliminated.clear();
}

/**
 * Scan the fixture's leaderboard for newly-eliminated bot corps and enqueue
 * epitaph modals for each. Idempotent: firing the same state twice does not
 * re-show the modal.
 */
export function checkEliminations(fixture: ClientFixture | null | undefined): void {
  const lb = fixture?.leaderboard;
  if (!Array.isArray(lb) || lb.length === 0) return;

  for (const c of lb) {
    if (!c.eliminated) continue;
    if (c.isMe) continue; // human player — no epitaph catalog entry
    if (seenEliminated.has(c.name)) continue;

    seenEliminated.add(c.name);

    const key = nameToKey?.get(c.name);
    // If the catalog hasn't loaded yet, or this name isn't in the catalog,
    // skip silently — better a missing epitaph than a raw i18n key on screen.
    if (!key) continue;

    showEpitaph({ corpKey: key, corpName: c.name });
  }
}
